import React from 'react';
import { Project } from '@/hooks/content-generator/workbook-types';
import { Card } from '@/components/ui/card';
import { Heart, Image, Clock } from 'lucide-react';
import { useWorkbookOperations } from '@/hooks/content-generator/use-workbook-operations';
import { useWorkbookNavigation } from '@/hooks/nav';
import { useConfirmationDialog } from '@/hooks/ui/use-confirmation-dialog';
import { cn } from '@/lib/utils';
import MoveCardButton from '@/components/ui/MoveCardButton';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  // Optional drag-and-drop props (matching File Storage pattern)
  isDraggable?: boolean;
  dragHandleProps?: Record<string, any>;
  isDragging?: boolean;
}

const ProjectCard: React.FC<ProjectCardProps> = React.memo(({ project, onClick, isDraggable, dragHandleProps, isDragging }) => {
  // Use unified hook without optimistic updates for stable behavior on the list page
  const { toggleFavorite, toggleArchive, deleteWorkbook } = useWorkbookOperations({ 
    optimisticUpdates: false 
  });
  
  // Use workbook navigation for consistent navigation patterns
  const { openWorkbook } = useWorkbookNavigation();
  
  // Use beautiful confirmation dialog
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog();
  
  const handleToggleFavorite = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite({ id: project.id, isFavorite: !project.isFavorite });
  }, [project.id, project.isFavorite, toggleFavorite]);
  
  // Get cover image from metadata if available
  const coverImage = project.metadata?.coverImage as string | undefined;

  // Format the updated date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // If dragging, show placeholder (matching File Storage pattern)
  if (isDragging) {
    return (
      <div className="opacity-50 bg-gray-200 dark:bg-gray-700 border-2 border-dashed border-purple-400 rounded-lg aspect-video" />
    );
  }

  return (
    <>
      <div
        className="group cursor-pointer transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
        onClick={onClick}
      >
        <Card 
          className={cn(
            "relative overflow-hidden rounded-2xl aspect-video",
            "transition-shadow duration-200 ease-out",
            "border-0 shadow-lg hover:shadow-xl",
            "bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800",
            project.isFavorite && "ring-2 ring-red-400/60 shadow-red-400/20"
          )}
        >
          {/* Background Image - Simplified */}
          {coverImage ? (
            <img
              src={coverImage} 
              alt={project.title} 
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200 ease-out"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-100 dark:from-indigo-950/40 dark:via-purple-950/40 dark:to-blue-950/40">
              <div className="text-gray-400/60 dark:text-gray-600/60 flex flex-col items-center">
                <Image className="w-12 h-12 mb-3 opacity-40" />
                <span className="text-sm font-medium opacity-60">No cover image</span>
              </div>
            </div>
          )}
          
          {/* Radial Overlay - Dark in bottom left corner */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(15,23,42,0.75),transparent_70%)]" />
          
          {/* Linear Gradient Overlay for Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-tr from-black/50 via-black/10 to-transparent" />
          
          {/* Status badges in top left corner - Simplified */}
          <div className="absolute top-3 left-3 z-20 flex flex-col gap-1.5">
            {/* Favorite badge - Simple version without complex animations */}
            {project.isFavorite && !project.isArchived && (
              <button
                onClick={handleToggleFavorite}
                className="flex items-center justify-center w-8 h-8 p-1.5 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg transition-all duration-200 hover:bg-white/30 hover:scale-105"
                title="Remove from favorites"
              >
                <Heart className="h-4 w-4 text-red-500 fill-red-500" />
              </button>
            )}
            
            {/* Last Edited Date - Top Left */}
            <div className="flex items-center gap-1 bg-black/30 backdrop-blur-sm rounded-md px-2 py-1">
              <Clock className="h-3 w-3 text-white/70" />
              <span className="text-xs text-white/70 font-medium">
                {formatDate(project.updatedAt)}
              </span>
            </div>
          </div>
          
          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="space-y-1">
              {/* Project Title */}
              <h3 className="font-semibold text-lg leading-tight line-clamp-2 drop-shadow-sm">
                {project.title}
              </h3>
              
              {/* Goal Title (if available) */}
              {project.goalTitle && (
                <p className="text-xs font-medium text-white/70 drop-shadow-sm">
                  {project.goalTitle}
                </p>
              )}
            </div>
          </div>

          {/* Top Right: Drag Handle (only when draggable, matching File Storage pattern) */}
          {isDraggable && dragHandleProps && (
            <div className="absolute top-2 right-2 z-20">
              <div
                {...dragHandleProps}
                className="cursor-grab active:cursor-grabbing touch-none"
                onClick={(e) => e.stopPropagation()}
              >
                <MoveCardButton
                  variant="glass"
                  size="sm"
                  iconType="move"
                  className="opacity-70 hover:opacity-100 transition-opacity duration-200"
                  title="Drag to reorder"
                />
              </div>
            </div>
          )}
        </Card>
      </div>
      
      {/* Beautiful Confirmation Dialog */}
      <ConfirmationDialog />
    </>
  );
});

export default ProjectCard;